import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { ApplicationConfig, LOCALE_ID, APP_INITIALIZER, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import localePt from '@angular/common/locales/pt';
import { MatDialogModule } from '@angular/material/dialog';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withComponentInputBinding, withRouterConfig } from '@angular/router';
import { MSAL_GUARD_CONFIG, MSAL_INSTANCE, MsalBroadcastService, MsalGuard, MsalModule, MsalService } from '@azure/msal-angular';
import { IPublicClientApplication, PublicClientApplication, } from '@azure/msal-browser';

import { routes } from './app.routes';
import { msalConfig, msalGuardConfig } from './core/config/msal.config';
import { authInterceptor } from './core/interceptors';

// Domain Providers
import { provideNgxMask } from 'ngx-mask';
import { CEP_PROVIDERS } from './domain/cep';
import { DOCUMENTO_PROVIDERS } from './domain/documento';
import { DOSSIER_PROVIDERS } from './domain/dossie';
import { ETAPAS_PROCESSO_PROVIDERS } from './domain/etapas-processo/etapas-processo.providers';
import { EVENTO_PROVIDERS } from './domain/evento';
import { PACOTE_DOSSIE_PROVIDERS } from './domain/pacote-dossie/pacote-dossie.providers';
import { PARTICIPANTE_PROVIDERS } from './domain/participante/participante.providers';
import { TIPO_DOCUMENTAL_PROVIDERS } from './domain/tipo-documental/tipo-documental.providers';
import { FEEDBACK_PROCESSO_PROVIDERS } from './domain/feedback-processo/feedback-processo.providers';
import { SIMULACAO_FINANCIAMENTO_PROVIDERS } from './domain/simulacao-financiamento/simulacao-financiamento.providers';
import { PROPOSTA_FINANCIAMENTO_PROVIDERS } from './domain/proposta-financiamento';
import { FGTS_SIUMP_PROVIDERS } from './domain/fgts-siump';

// Auth Domain Providers
import { AuthRepository } from './domain/auth/repositories/auth.repository';
import { AuthRepositoryImpl } from './domain/auth/repositories/auth.repository.impl';
import {
  InicializarMsalUseCase,
  RealizarLoginUseCase,
  ObterUsuarioUseCase,
  VerificarAutenticacaoUseCase
} from './domain/auth/usecases';

// Core Services
import { MsalService as CustomMsalService } from './core/services/msal.service';
import { AppInitializerService } from './core/services/app-initializer.service';

// Features
import { AuthViewModel } from './features/auth/viewmodels/auth.viewmodel';
// Registra o locale pt-BR
registerLocaleData(localePt);

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication(msalConfig);
}

export function AppInitializerFactory(appInitializer: AppInitializerService) {
  return () => appInitializer.initialize();
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withComponentInputBinding(), withRouterConfig({ paramsInheritanceStrategy: 'always' })),
    provideNgxMask({
      validation: true,
      dropSpecialCharacters: false,
    }),
    provideAnimationsAsync(),

    // Existing domain providers
    { provide: LOCALE_ID, useValue: 'pt-BR' },
    ...CEP_PROVIDERS,
    ...TIPO_DOCUMENTAL_PROVIDERS,
    ...PARTICIPANTE_PROVIDERS,
    ...ETAPAS_PROCESSO_PROVIDERS,
    ...DOSSIER_PROVIDERS,
    ...PACOTE_DOSSIE_PROVIDERS,
    ...DOCUMENTO_PROVIDERS,
    ...EVENTO_PROVIDERS,
    ...FEEDBACK_PROCESSO_PROVIDERS,
    ...SIMULACAO_FINANCIAMENTO_PROVIDERS,
    ...PROPOSTA_FINANCIAMENTO_PROVIDERS,
    ...FGTS_SIUMP_PROVIDERS,
    provideHttpClient(withInterceptors([authInterceptor])),

    // Material Design modules
    importProvidersFrom(MsalModule, MatDialogModule, MatSnackBarModule),

    // MSAL Configuration
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useValue: msalGuardConfig
    },
    // MSAL_INTERCEPTOR_CONFIG removido - usando interceptor customizado

    // MSAL Services (keeping existing for compatibility)
    MsalService,
    MsalGuard,
    MsalBroadcastService,

    // Our custom Auth implementation
    {
      provide: AuthRepository,
      useExisting: AuthRepositoryImpl
    },

    // Auth Use Cases
    InicializarMsalUseCase,
    RealizarLoginUseCase,
    ObterUsuarioUseCase,
    VerificarAutenticacaoUseCase,

    // Core Services
    CustomMsalService,

    // ViewModels
    AuthViewModel,

    // App Initializer - garante que MSAL seja inicializado antes da aplicação
    AppInitializerService,
    {
      provide: APP_INITIALIZER,
      useFactory: AppInitializerFactory,
      deps: [AppInitializerService],
      multi: true
    }
  ]
};
